import { ICampaignTriggerKeyword } from '@dataTypes/marketing.type'
import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from '../errors/api-error'

// Constants
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_MARKETING', '')

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type CampaignsTriggerData = {
  campaign_trigger_keywords?: ICampaignTriggerKeyword[]
}

type GenericResponseData = Record<string, unknown>

type UpdateCampaignTriggerResponse = ApiResponse<CampaignsTriggerData>
type CreateCampaignTriggerResponse = ApiResponse<GenericResponseData>
type CreateMultipleCampaignTriggerResponse = ApiResponse<CampaignsTriggerData>

// API Functions
export const updateCampaignTriggerKeywordById = async (
  campaign_trigger_keyword_id: string,
  updatedData: Partial<ICampaignTriggerKeyword>,
): Promise<UpdateCampaignTriggerResponse> => {
  try {
    const response = await axios.put<UpdateCampaignTriggerResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/trigger-keyword`,
      { campaign_trigger_keyword_id, data: updatedData },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateCampaignTriggerKeywordById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateMultipleTriggerKeywords = async (
  keywords: ICampaignTriggerKeyword[],
): Promise<UpdateCampaignTriggerResponse> => {
  try {
    const response = await axios.put<UpdateCampaignTriggerResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/trigger-keyword/bulk`,
      { campaign_trigger_keywords: keywords },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateMultipleTriggerKeywords:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createTriggerKeyword = async (
  triggerDetails: ICampaignTriggerKeyword,
): Promise<CreateCampaignTriggerResponse> => {
  try {
    const response = await axios.post<CreateCampaignTriggerResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/trigger-keyword`,
      triggerDetails,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in createCampaignTriggerKeyword:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createMultipleTriggerKeywords = async (
  keywords: ICampaignTriggerKeyword[],
): Promise<CreateMultipleCampaignTriggerResponse> => {
  try {
    const response = await axios.post<CreateMultipleCampaignTriggerResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/trigger-keyword/bulk`,
      { campaign_trigger_keywords: keywords },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in createMultipleTriggerKeywords:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteTriggerKeywordById = async (
  campaignTriggerKeywordId: string,
): Promise<CreateCampaignTriggerResponse> => {
  try {
    const response = await axios.delete<CreateCampaignTriggerResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/trigger-keyword/${campaignTriggerKeywordId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteCampaignTriggerKeywordById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteMultipleTriggerKeywords = async (
  campaignTriggerKeywordIds: string[],
): Promise<CreateCampaignTriggerResponse> => {
  try {
    const response = await axios.delete<CreateCampaignTriggerResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/trigger-keyword/bulk`,
      {
        headers,
        data: {
          campaign_trigger_keyword_ids: campaignTriggerKeywordIds,
        },
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteMultipleTriggerKeywords:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
